import { company } from '~/company/Company';

import type { AnalyticsProvider } from '../types';

interface DataLayerWindow extends Window {
  dataLayer?: Array<Record<string, unknown>>;
}

/**
 * Clears keys from GTM's data model.
 *
 * GTM merges every pushed key into a model that survives until the page is
 * unloaded, so a value pushed once stays readable by every tag that fires
 * afterwards. For sensitive keys that lifetime is the problem, and pushing
 * `undefined` is the documented way to end it.
 */
export const resetDataLayerKeys = (keys: string[]): void => {
  if (typeof window === 'undefined') return;

  const w = window as DataLayerWindow;
  if (!w.dataLayer) return;

  w.dataLayer.push(Object.fromEntries(keys.map((key) => [key, undefined])));
};

export class GtmProvider implements AnalyticsProvider {
  readonly name = 'gtm' as const;
  readonly requiresConsent = true;

  isAvailable(): boolean {
    return (
      typeof window !== 'undefined' &&
      Boolean(company.envVariables.REACT_APP_GTM_ID)
    );
  }

  initialize(): void {
    // dataLayer is initialized by the GTM bootstrap script injected in server.js
  }

  track(eventName: string, payload: Record<string, unknown>): void {
    const w = window as DataLayerWindow;
    if (!w.dataLayer) {
      w.dataLayer = [];
    }
    w.dataLayer.push({ event: eventName, ...payload });
  }
}
